// src/components/ReportsComponent.tsx
import { useState, useEffect, useRef } from 'react';
import {
  DataGrid,
  GridColDef,
} from '@mui/x-data-grid';
import {
  Box,
  Typography,
  CircularProgress,
  GlobalStyles,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  AlertColor,
  Chip,
  Stack,
  Divider,
  Button,
} from '@mui/material';
import Fab from '@mui/material/Fab';
import { RestartAlt, ArrowUpward, HealingOutlined, PushPin, PushPinOutlined } from '@mui/icons-material';
import { motion } from 'framer-motion';
import axios from 'axios';
import { formatDateOnly, formatTimeOnly } from '../utils/dateUtils';
import { useAuth } from './AuthContext';
import { dataGridSx, dataGridContainerSx } from '../utils/tableStyles';
import { usePinnedApps } from '../utils/usePinnedApps';
import { matchesCurrentUser } from '../utils/userMatch';
import { logAudit } from '../utils/audit';
import { fetchDashboardTick } from '../utils/dashboardTick';
import { CompactPipelineProgress } from './CompactPipelineProgress';
import { CancelPipelineButton } from './CancelPipelineButton';
import { BuildErrorDrawer, BuildErrorTarget } from './BuildErrorDrawer';
import { usePipelineContext } from '../contexts/PipelineContext';
import type { PipelineBuild } from '../contexts/PipelineContext';

/* ------------------------------------------------------------------
 * Types
 * ------------------------------------------------------------------ */
type PipelineData = {
  id: number;              // AppManager build ID (DB row)
  pipelineId?: number;     // GitLab pipeline ID (retry target)
  composerHost: string;    // Added for Composer Host display
  appId?: string | number; // D1 App ID
  phase: string;           // The text name of the phase (e.g. npm_build)
  phaseStatus: string;     // The status of that phase (e.g. success)
  pipelineStatus: string;  // The overall Pipeline Status (e.g. running, failed)
  version: string;
  osSpecification: string; // "android" | "ios" | etc.
  status: string;          // Raw legacy status
  dateTimeStamp: string;   // ISO
  submitter: string;
  pipelineUrl: string;
};

type ReportsComponentProps = {
  url?: string; // Optional now
  isActive: boolean;
};


/* ------------------------------------------------------------------
 * Status helpers
 * ------------------------------------------------------------------ */
const isFailedStatus = (raw?: string) => {
  const s = (raw ?? '').toLowerCase();
  return s.includes('fail') || s.includes('cancel'); // treat ANY "fail*" as failed
};

const normalizeStatus = (raw?: string) => {
  const s = (raw ?? '').trim().toLowerCase();
  if (isFailedStatus(s)) return s.includes('cancel') ? 'Cancelled' : 'Failed';
  if (s === 'success') return 'Success';
  if (s === 'manual') return 'Manual';
  // User Req: If queued, show Running until success/failure
  if (s === 'queued' || s === 'running' || s === 'pending') return 'Running';
  return raw || 'Running';
};

/* ==================================================================
 * Component
 * ================================================================== */
type FeedFilter = 'all' | 'failed' | 'running' | 'mine' | 'lastHour';

export const ReportComponent = ({ isActive }: ReportsComponentProps) => {
  const { dashboardData, userInfo } = useAuth();
  const { updateRecentBuilds } = usePipelineContext();

  const TERMINAL_STATUSES = new Set(['success', 'failed', 'cancelled', 'canceled', 'manual']);

  /* ----------------------------------------------------------------
   * State
   * ---------------------------------------------------------------- */
  const [rows, setRows] = useState<PipelineData[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 15 });
  const [loading, setLoading] = useState(false);

  // Retry state
  const [retryingPipelineId, setRetryingPipelineId] = useState<number | null>(null);

  // Reconcile stale builds state
  const [isReconciling, setIsReconciling] = useState(false);
  const [lastReconciledAt, setLastReconciledAt] = useState<string | null>(
    () => localStorage.getItem('reconcileStaleLastRunAt')
  );

  // Quick filter chips above the table — operates client-side on the current
  // page. Honest UX: shows "N of M on this page" so the user knows the filter
  // is page-local, not cross-pagination.
  const [feedFilter, setFeedFilter] = useState<FeedFilter>('all');
  // App-version filter — client-side over the current page (same scope as the
  // chips above). '' = all versions.
  // Per-build error/log drawer (core reason + full GitLab log), opened by
  // clicking a row in the feed.
  const [errorDrawerBuild, setErrorDrawerBuild] = useState<BuildErrorTarget | null>(null);

  // Pinned apps come from a shared hook that listens for same-tab and
  // cross-tab updates, so toggling here updates the Home overview panel
  // immediately and vice-versa.
  const { pinned, togglePin } = usePinnedApps();

  // Snackbar
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState('');
  const [snackSeverity, setSnackSeverity] = useState<AlertColor>('info');

  // Scroll anchors
  const topRef = useRef<HTMLDivElement | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const [showScrollUp, setShowScrollUp] = useState(false);

  // Refs for polling — always hold the latest values without re-creating the interval
  const rowsRef = useRef<PipelineData[]>([]);
  const isSystemIdleRef = useRef<boolean>(false);
  useEffect(() => { rowsRef.current = rows; }, [rows]);
  useEffect(() => { isSystemIdleRef.current = !!dashboardData?.isSystemIdle; }, [dashboardData]);

  /* ----------------------------------------------------------------
   * Snackbar helpers
   * ---------------------------------------------------------------- */
  const openSnack = (msg: string, severity: AlertColor) => {
    setSnackMsg(msg);
    setSnackSeverity(severity);
    setSnackOpen(true);
  };
  const handleSnackClose = (_?: unknown, reason?: string) => {
    if (reason === 'clickaway') return;
    setSnackOpen(false);
  };

  /* ----------------------------------------------------------------
   * Smooth scrolling util
   * ---------------------------------------------------------------- */
  const handleScrollUp = () => {
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  /* ----------------------------------------------------------------
   * Status → Chip
   * ---------------------------------------------------------------- */
  const renderStatusChip = (statusValue: string) => {
    if (isFailedStatus(statusValue)) {
      const label = normalizeStatus(statusValue);
      return <Chip label={label} color="error" size="small" sx={{ fontWeight: 'bold', textTransform: 'capitalize' }} />;
    }
    const s = statusValue.toLowerCase();
    if (s === 'success') {
      return <Chip label="Success" color="success" size="small" sx={{ fontWeight: 'bold', textTransform: 'capitalize' }} />;
    }

    // Treat 'queued' as 'Running' for display color (or keep warning if preferred, but user asked to change status text to Running)
    // If text is Running, color should probably be primary (blue) or standard.
    const normalized = normalizeStatus(statusValue);
    if (normalized === 'Running') {
      // Using primary for running
      return <Chip label="Running" color="primary" size="small" sx={{ fontWeight: 'bold', textTransform: 'capitalize' }} />;
    }

    return <Chip label={normalized} color="primary" size="small" sx={{ fontWeight: 'bold', textTransform: 'capitalize' }} />;
  };

  /* ----------------------------------------------------------------
   * Fetch Data
   * silent=true → used by the poll; skips the loading spinner
   * ---------------------------------------------------------------- */
  const fetchFeed = async (silent = false) => {
    if (!isActive) return;
    // The idle flag exists to spare D1 from the 8 s background poll when nothing
    // is happening — but it must not block a user-initiated/navigation fetch
    // (silent=false). Otherwise, arriving on the feed right after triggering a
    // build shows stale rows until a full page reload re-derives idle=false.
    if (silent && isSystemIdleRef.current) {
      console.log('💤 [Frontend] System Idle: Skipping background activity feed poll.');
      return;
    }
    if (!silent) setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.append('limit', paginationModel.pageSize.toString());
      params.append('offset', (paginationModel.page * paginationModel.pageSize).toString());

      const response = await axios.get(`/api/builds/summary?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      const { items, metadata } = response.data;

      const TERMINAL_BUILD_STATUSES = new Set(['success', 'failed', 'cancelled', 'canceled']);

      const mappedRows: PipelineData[] = (items || []).map((b: any) => {
        const buildStatus = (b.pipelineStatus || '').toLowerCase();
        const isTerminal = TERMINAL_BUILD_STATUSES.has(buildStatus);

        // The summary endpoint returns currentPhase already resolved (running
        // phase for in-flight builds, or the status string for terminal ones).
        // Mirror the previous chip behaviour: terminal builds carry the build
        // status as the phase status; in-flight builds are treated as running.
        const rawPhase = b.currentPhase ?? '';
        const phaseName = rawPhase
          || (isTerminal ? buildStatus.charAt(0).toUpperCase() + buildStatus.slice(1) : 'Initializing');
        const phaseStatus = isTerminal
          ? buildStatus
          : (buildStatus === 'queued' ? 'queued' : 'running');

        return {
          id: b.buildId,
          pipelineId: undefined,
          composerHost: b.composerHost || 'Unknown',
          appId: b.appId ?? 'N/A',
          phase: phaseName,
          phaseStatus,
          pipelineStatus: b.pipelineStatus || 'unknown',
          version: b.version || '',
          osSpecification: b.os || '',
          status: b.pipelineStatus || '',
          dateTimeStamp: b.createdAt || '',
          submitter: b.submitter || '',
          pipelineUrl: b.pipelineUrl || '',
        };
      });

      setRows(mappedRows);
      setRowCount(metadata?.total ?? 0);

      // Push into PipelineContext so CompactPipelineProgress picks up live status/phase
      const pipelineBuilds: PipelineBuild[] = mappedRows.map(row => ({
        buildId: row.id,
        appId: typeof row.appId === 'number' ? row.appId : parseInt(String(row.appId)) || 0,
        composerHost: row.composerHost,
        status: row.pipelineStatus,
        phase: row.phase,
        version: row.version,
        os: row.osSpecification,
        dateTime: row.dateTimeStamp,
        pipelineUrl: row.pipelineUrl,
        pipelineId: row.pipelineId,
      }));
      updateRecentBuilds(pipelineBuilds);
    } catch (error: any) {
      console.error('Feed fetch error:', error);
      if (!silent) openSnack('Failed to load activity feed.', 'error');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Initial load + re-fetch on pagination change
  useEffect(() => {
    fetchFeed();
  }, [isActive, paginationModel]);

  // Auto-close the per-build drawer when the underlying page of rows changes
  // (pagination) or the user navigates away from the feed (isActive=false) —
  // otherwise the drawer would describe a build that's no longer in view.
  useEffect(() => {
    setErrorDrawerBuild(null);
  }, [paginationModel.page, paginationModel.pageSize, isActive]);

  // Silent poll using the optimised /api/builds/dashboard-tick endpoint.
  // Why the switch from the old /summary fetch on every tick:
  //   • /summary returns the FULL current page on every call (≈ 15 rows ×
  //     ~1.5 KB) and runs the same D1 query each cycle.
  //   • dashboard-tick returns only builds whose updatedAt > the timestamp
  //     of the previous tick, plus a single round-trip for app-status that
  //     TableComponent's separate 12 s poll used to do.
  // On a quiet dashboard that means ~0 bytes of build data instead of ~22 KB
  // every 8 s, and one D1 query instead of three.
  //
  // The full /summary path stays the source of truth for first-load,
  // pagination changes, and any case where the delta cap is exceeded — the
  // fallback to fetchFeed(true) below handles that.
  const tickFeedSinceRef = useRef<string | null>(null);
  // Reset the delta cursor whenever the visible page changes — the rows the
  // user is looking at change wholesale, so a delta against the previous
  // serverTime would skip pre-existing rows on the new page.
  useEffect(() => { tickFeedSinceRef.current = null; }, [paginationModel.page, paginationModel.pageSize]);

  useEffect(() => {
    if (!isActive) return;
    const id = setInterval(async () => {
      const hasRunning = rowsRef.current.some(
        r => !TERMINAL_STATUSES.has((r.pipelineStatus || '').toLowerCase())
      );
      if (!hasRunning) return;

      // First post-mount tick on this page has no `since` yet — let the
      // existing /summary path do the initial pull so the user sees the
      // full page. Subsequent ticks use the delta endpoint.
      if (!tickFeedSinceRef.current) {
        tickFeedSinceRef.current = new Date().toISOString();
        fetchFeed(true);
        return;
      }

      try {
        const since = tickFeedSinceRef.current;
        const tick = await fetchDashboardTick({ feedSince: since, feedLimit: 50 });
        tickFeedSinceRef.current = tick.serverTime;

        // If the delta hit the cap, the page may have shifted enough that
        // an in-place merge would be misleading — pull a fresh /summary
        // page instead. Cheap signal, zero false positives.
        if (tick.feedHasMore) {
          fetchFeed(true);
          return;
        }
        if (tick.feedDelta.length === 0) return;

        // Merge the delta into the current rows. Each delta item carries
        // the same field shape mappedRows uses (see fetchFeed) so the
        // transform mirrors it.
        const updates = new Map<number, PipelineData>();
        for (const b of tick.feedDelta) {
          const buildStatus = (b.pipelineStatus || '').toLowerCase();
          const isTerminalDelta = TERMINAL_STATUSES.has(buildStatus);
          const rawPhase = b.currentPhase ?? '';
          const phaseName = rawPhase
            || (isTerminalDelta ? buildStatus.charAt(0).toUpperCase() + buildStatus.slice(1) : 'Initializing');
          const phaseStatus = isTerminalDelta
            ? buildStatus
            : (buildStatus === 'queued' ? 'queued' : 'running');
          updates.set(b.buildId, {
            id: b.buildId,
            pipelineId: undefined,
            composerHost: b.composerHost || 'Unknown',
            appId: b.appId ?? 'N/A',
            phase: phaseName,
            phaseStatus,
            pipelineStatus: b.pipelineStatus || 'unknown',
            version: b.version || '',
            osSpecification: b.os || '',
            status: b.pipelineStatus || '',
            dateTimeStamp: typeof b.createdAt === 'string' ? b.createdAt : String(b.createdAt),
            submitter: b.submitter || '',
            pipelineUrl: b.pipelineUrl || '',
          });
        }
        setRows(prev => {
          const next = prev.map(r => updates.get(r.id) ?? r);
          // Surface brand-new builds at the top — but only on the first page.
          // With server-side pagination, `prev` is just the current page slice,
          // so prepending on page > 0 would inject page-0 rows into a later
          // page and overflow its size. Off the first page, only in-place
          // status updates are applied; new builds appear when the user pages
          // back to the top or the next fetch runs.
          if (paginationModel.page === 0) {
            const known = new Set(prev.map(r => r.id));
            for (const [id, row] of updates) if (!known.has(id)) next.unshift(row);
          }
          return next;
        });
      } catch (err) {
        // Fall back to the legacy path on any error — the user shouldn't
        // see a stale feed because the optimised endpoint blipped.
        console.warn('[dashboard-tick] poll failed, falling back to /summary:', err);
        fetchFeed(true);
      }
    }, 8000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, paginationModel]);

  // Auto-reconcile loop. The 8 s poll above re-fetches from D1 but D1 itself
  // can be stale when a GitLab webhook delivery is dropped (we've seen builds
  // sit at "Running" for hours after GitLab finished them). Strategy:
  //   • One unconditional sweep ~3 s after mount, after the first feed load
  //     has populated rowsRef. Heals a stuck row before the operator notices.
  //   • Periodic ticks every 60 s while the tab is open, but only if at
  //     least one visible non-terminal row is older than STALE_THRESHOLD_MS.
  //   • Both gated by RECONCILE_MIN_INTERVAL_MS so we don't hammer the
  //     endpoint across multiple tabs / fast user navigation.
  //   • lastReconciledAt is updated locally so the "Resolve Stale Builds"
  //     button's last-run timestamp reflects auto runs too — otherwise
  //     operators have no way to tell the dashboard is self-healing.
  const STALE_THRESHOLD_MS = 2 * 60 * 1000;       // 2 min — surface fresh data fast
  const RECONCILE_MIN_INTERVAL_MS = 60 * 1000;    // at most once per 60 s
  const INITIAL_RECONCILE_DELAY_MS = 3 * 1000;    // wait for first fetchFeed
  const lastAutoReconcileRef = useRef<number>(0);
  useEffect(() => {
    if (!isActive) return;
    let cancelled = false;

    const runReconcile = async () => {
      const now = Date.now();
      if (now - lastAutoReconcileRef.current < RECONCILE_MIN_INTERVAL_MS) return;
      lastAutoReconcileRef.current = now;
      try {
        const tok = localStorage.getItem('token');
        await axios.post(
          '/api/builds/reconcile-stale',
          {},
          { headers: tok ? { Authorization: `Bearer ${tok}` } : {} },
        );
        if (cancelled) return;
        // Reflect the run in the "Resolve Stale Builds" button so operators
        // can see the dashboard is keeping itself fresh. Persisted across
        // reloads via the same localStorage key the manual button uses.
        const stamp = new Date().toISOString();
        setLastReconciledAt(stamp);
        try { localStorage.setItem('reconcileStaleLastRunAt', stamp); } catch { /* ignore */ }
        // The backend invalidates the analytics_feed cache after a
        // reconcile that changed something, so the next fetchFeed will hit
        // D1 directly.
        fetchFeed(true);
      } catch (err) {
        console.warn('[auto-reconcile] failed:', err);
      }
    };

    // Initial sweep — fires once shortly after mount so a stuck row on a
    // freshly-opened tab heals within seconds rather than after the first
    // periodic tick (which would be up to 60 s later).
    const initialId = setTimeout(() => { if (!cancelled) runReconcile(); }, INITIAL_RECONCILE_DELAY_MS);

    // Periodic guard — only runs if a non-terminal row has been hanging
    // around for STALE_THRESHOLD_MS without flipping to a terminal state.
    const tick = () => {
      const now = Date.now();
      const stale = rowsRef.current.some(r => {
        const status = (r.pipelineStatus || '').toLowerCase();
        if (TERMINAL_STATUSES.has(status)) return false;
        const t = new Date(r.dateTimeStamp || '').getTime();
        if (!Number.isFinite(t)) return false;
        return (now - t) > STALE_THRESHOLD_MS;
      });
      if (stale) runReconcile();
    };
    const periodicId = setInterval(tick, 60 * 1000);

    return () => {
      cancelled = true;
      clearTimeout(initialId);
      clearInterval(periodicId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, paginationModel]);

  /* ----------------------------------------------------------------
   * Retry pipeline
   * ---------------------------------------------------------------- */
  const handleRetry = async (row: PipelineData) => {
    const retryTarget = row.pipelineId ?? row.id;
    setRetryingPipelineId(retryTarget);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `/api/appdata/restart-pipeline?pipelineId=${encodeURIComponent(String(retryTarget))}`,
        {},
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      openSnack('Pipeline restart requested.', 'success');
      logAudit({
        action: 'retry',
        targetType: 'pipeline',
        targetId: String(retryTarget),
        metadata: {
          composerHost: row.composerHost,
          appId: row.appId,
          version: row.version,
          platform: row.osSpecification,
          previousStatus: row.status,
        },
      });
      fetchFeed(); // Refresh list
    } catch {
      openSnack('Error restarting pipeline.', 'error');
    } finally {
      setRetryingPipelineId(null);
    }
  };

  /* ----------------------------------------------------------------
   * Reconcile stale/stuck builds
   * ---------------------------------------------------------------- */
  const handleReconcileStale = async () => {
    setIsReconciling(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `/api/builds/reconcile-stale`,
        {},
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      const s = response.data?.summary ?? {};
      const scanned = response.data?.scanned ?? 0;
      const parts = [
        `scanned ${scanned}`,
        `reconciled ${s.reconciled ?? 0}`,
        `stillActive ${s.stillActive ?? 0}`,
        `notTriggered ${s.pipelineNotTriggered ?? 0}`,
        `notFound ${s.pipelineNotFound ?? 0}`,
        `unchanged ${s.unchanged ?? 0}`,
      ];
      openSnack(`Stale builds resolved — ${parts.join(', ')}.`, 'success');
      const now = new Date().toISOString();
      setLastReconciledAt(now);
      localStorage.setItem('reconcileStaleLastRunAt', now);
      logAudit({
        action: 'reconcile_stale',
        targetType: 'session',
        targetId: now,
        metadata: { scanned, ...s },
      });
      fetchFeed(true);
    } catch (error: any) {
      const msg = error?.response?.data?.error || error?.message || 'Failed to resolve stale builds.';
      openSnack(msg, 'error');
    } finally {
      setIsReconciling(false);
    }
  };

  /* ----------------------------------------------------------------
   * Scroll Listener
   * ---------------------------------------------------------------- */
  useEffect(() => {
    const onScroll = () => setShowScrollUp(window.scrollY > 300);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* ----------------------------------------------------------------
   * DataGrid columns (disableColumnMenu: true)
   * ---------------------------------------------------------------- */
  const columns: GridColDef[] = [
    {
      field: 'pin',
      headerName: '',
      width: 50,
      headerAlign: 'center',
      align: 'center',
      disableColumnMenu: true,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const host = (params.row as PipelineData).composerHost;
        if (!host) return null;
        const isPinned = pinned.includes(host);
        // Pinning is host-level: pinning *any* row of `schoolbuzz.sites.finalsite.dev`
        // pins the whole composer host, so every row for that host correctly
        // reflects the pinned state. The tooltip spells out the host so the
        // user understands it's not a per-build action.
        return (
          <Tooltip
            title={
              isPinned
                ? `Unpin ${host} from your watch list (affects all builds for this host)`
                : `Pin ${host} to your watch list (will show all builds for this host)`
            }
            arrow
            placement="right"
          >
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                togglePin(host);
                openSnack(
                  isPinned ? `Unpinned ${host}` : `Pinned ${host} — view on Home page`,
                  isPinned ? 'info' : 'success',
                );
              }}
              sx={{ color: isPinned ? '#6366f1' : 'text.secondary' }}
            >
              {isPinned ? <PushPin sx={{ fontSize: 18 }} /> : <PushPinOutlined sx={{ fontSize: 18 }} />}
            </IconButton>
          </Tooltip>
        );
      },
    },
    { field: 'id', headerName: 'Build ID', width: 90, headerAlign: 'center', align: 'center', disableColumnMenu: true, sortable: false },
    { field: 'appId', headerName: 'App ID', width: 90, headerAlign: 'center', align: 'center', disableColumnMenu: true, sortable: false },
    { field: 'composerHost', headerName: 'Composer Host', width: 220, headerAlign: 'center', align: 'center', disableColumnMenu: true, sortable: false },

    // Phase Column (Chip + Name)
    {
      field: 'phase',
      headerName: 'Phase',
      width: 250,
      headerAlign: 'center',
      align: 'center',
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params) => {
        const row = params.row as PipelineData;
        const phaseName = row.phase || 'N/A';
        const phaseStatus = row.phaseStatus || 'pending';

        return (
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ width: '100%', height: '100%' }}>
            <Box sx={{ minWidth: 80, display: 'flex', justifyContent: 'center' }}>
              {renderStatusChip(phaseStatus)}
            </Box>
            <Divider orientation="vertical" flexItem sx={{ height: '60%', alignSelf: 'center', bgcolor: 'rgba(0,0,0,0.1)' }} />
            <Typography variant="body2" sx={{ fontWeight: 500, textTransform: 'capitalize', color: 'text.primary', textAlign: 'center' }}>
              {phaseName.replace(/_/g, ' ')}
            </Typography>
          </Stack>
        );
      },
    },

    { field: 'version', headerName: 'Version', width: 110, headerAlign: 'center', align: 'center', disableColumnMenu: true, sortable: false },
    { field: 'osSpecification', headerName: 'OS', width: 90, headerAlign: 'center', align: 'center', disableColumnMenu: true, sortable: false },

    { field: 'submitter', headerName: 'Submitter', width: 150, headerAlign: 'center', align: 'center', disableColumnMenu: true, sortable: false },

    {
      field: 'dateTimeStamp',
      headerName: 'Date & Time',
      width: 140,
      headerAlign: 'center',
      align: 'center',
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>{formatDateOnly(params.value as string)}</Typography>
          <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.75rem' }}>{formatTimeOnly(params.value as string)}</Typography>
        </Box>
      ),
    },
    {
      field: 'pipelineUrl',
      headerName: 'Pipeline URL',
      width: 130,
      headerAlign: 'center',
      align: 'center',
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params) => {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {params.value ? (
              <a href={params.value as string} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: '#1976d2', fontWeight: 500 }}>
                View Log
              </a>
            ) : (
              <span style={{ opacity: 0.5 }}>N/A</span>
            )}
          </Box>
        );
      },
    },

    // Build Progress
    {
      field: 'buildProgress',
      headerName: 'Build Progress',
      width: 240,
      headerAlign: 'center',
      align: 'center',
      disableColumnMenu: true,
      sortable: false,
      valueGetter: (_value: any, row: any) => row.pipelineStatus || '',
      renderCell: (params) => {
        const row = params.row as PipelineData;
        const status = row.pipelineStatus || '';
        const phase = row.phase || '';

        // No status at all → nothing to show
        if (!status || status === 'unknown') {
          return (
            <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>—</Typography>
            </Box>
          );
        }

        // Use the real pipelineId when available; fall back to a stable synthetic key
        const pipelineId = row.pipelineId
          ? String(row.pipelineId)
          : `feed-${row.id}`;

        return (
          <Box sx={{ width: '100%', height: '100%', px: 1, display: 'flex', alignItems: 'center' }}>
            <CompactPipelineProgress
              pipelineId={pipelineId}
              domainId={row.composerHost}
              platform={row.osSpecification}
              initialStatus={status}
              initialPhase={phase}
              buildId={row.id}
            />
          </Box>
        );
      },
    },

    // Overall Pipeline Status
    {
      field: 'pipelineStatus',
      headerName: 'Pipeline Status',
      width: 150,
      headerAlign: 'center',
      align: 'center',
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params) => {
        const row = params.row as PipelineData;
        const status = row.pipelineStatus || 'Unknown';
        const failed = isFailedStatus(status);
        const isRunning = /queue|running|pending/i.test(status);
        const isRetrying = retryingPipelineId === (row.pipelineId ?? row.id);

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
            <Stack direction="row" spacing={1} alignItems="center">
              {renderStatusChip(status)}
              {failed && (
                <Tooltip title={isRetrying ? 'Retrying...' : 'Retry pipeline'}>
                  <span>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleRetry(row); }} disabled={isRetrying}>
                      {isRetrying ? <CircularProgress size={14} /> : <RestartAlt fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {isRunning && (
                <CancelPipelineButton
                  buildId={row.id}
                  label={row.composerHost}
                  version={row.version}
                  platform={row.osSpecification}
                  onCancelled={() => fetchFeed(true)}
                />
              )}
            </Stack>
          </Box>
        );
      },
    },
  ];

  /* ----------------------------------------------------------------
   * Render
   * ---------------------------------------------------------------- */
  return (
    <>
      <GlobalStyles
        styles={{
          '@media (hover:hover)': { '.MuiDataGrid-row:hover': { transform: 'scale(1.005)', transition: 'transform 120ms ease', zIndex: 1 } },
        }}
      />

      <Snackbar open={snackOpen} autoHideDuration={4000} onClose={handleSnackClose}>
        <Alert onClose={handleSnackClose} severity={snackSeverity} variant="filled">{snackMsg}</Alert>
      </Snackbar>

      <div ref={topRef} />

      <Box sx={{ width: '100%', px: 3, pt: 2, pb: 4 }}>
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <Typography variant="h5" mb={1} textAlign="center" sx={{ fontWeight: 600, color: 'text.primary' }}>
            Recent Activity Feed
          </Typography>

          {(() => {
            // Filter chip set — handlers update feedFilter. Counts come from
            // the current page so the user knows what each chip will surface.
            const hasUser = !!(userInfo && (userInfo.email || userInfo.preferred_username || userInfo.name));
            const hourAgo = Date.now() - 60 * 60 * 1000;
            const matchesFilter = (r: PipelineData, f: FeedFilter): boolean => {
              if (f === 'all') return true;
              const s = (r.pipelineStatus || r.status || '').toLowerCase();
              if (f === 'failed') return /fail|cancel/.test(s);
              if (f === 'running') return /queue|running|pending/.test(s);
              if (f === 'mine') return hasUser && matchesCurrentUser(r.submitter, userInfo);
              if (f === 'lastHour') {
                const t = new Date(r.dateTimeStamp || '').getTime();
                return Number.isFinite(t) && t >= hourAgo;
              }
              return true;
            };
            const FILTERS: Array<{ key: FeedFilter; label: string }> = [
              { key: 'all', label: 'All' },
              { key: 'failed', label: 'Failed' },
              { key: 'running', label: 'Running' },
              { key: 'mine', label: 'My builds' },
              { key: 'lastHour', label: 'Last hour' },
            ];
            const base = rows;
            const counts: Record<FeedFilter, number> = {
              all: base.length,
              failed: base.filter(r => matchesFilter(r, 'failed')).length,
              running: base.filter(r => matchesFilter(r, 'running')).length,
              mine: base.filter(r => matchesFilter(r, 'mine')).length,
              lastHour: base.filter(r => matchesFilter(r, 'lastHour')).length,
            };
            return (
              <Stack
                data-tour="home-feed"
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                sx={{ mb: 1.5, rowGap: 1 }}
              >
                {FILTERS.map(f => {
                  const isMine = f.key === 'mine';
                  const disabled = isMine && !hasUser;
                  const active = feedFilter === f.key;
                  return (
                    <Tooltip
                      key={f.key}
                      title={disabled ? 'Sign in to filter by your builds' : `${counts[f.key]} on this page`}
                      arrow
                      disableHoverListener={!disabled && active}
                    >
                      <span>
                        <Chip
                          label={`${f.label}${counts[f.key] > 0 ? ` · ${counts[f.key]}` : ''}`}
                          size="small"
                          disabled={disabled}
                          onClick={() => !disabled && setFeedFilter(f.key)}
                          sx={{
                            cursor: disabled ? 'not-allowed' : 'pointer',
                            fontWeight: 700,
                            bgcolor: active ? 'rgba(99, 102, 241, 0.14)' : 'rgba(15, 23, 42, 0.04)',
                            color: active ? '#4338ca' : 'text.secondary',
                            border: active ? '1px solid rgba(99, 102, 241, 0.5)' : '1px solid transparent',
                            '&:hover': { bgcolor: active ? 'rgba(99, 102, 241, 0.18)' : 'rgba(15, 23, 42, 0.08)' },
                          }}
                        />
                      </span>
                    </Tooltip>
                  );
                })}
                {feedFilter !== 'all' && counts[feedFilter] < base.length && (
                  <Typography variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>
                    Showing {counts[feedFilter]} of {base.length} on this page
                  </Typography>
                )}
              </Stack>
            );
          })()}

          <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
            {lastReconciledAt && (
              <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                Last run: {formatDateOnly(lastReconciledAt)} {formatTimeOnly(lastReconciledAt)}
              </Typography>
            )}
            <Tooltip title="Scan for builds stuck in queued/running for over 3 hours and reconcile their status with GitLab.">
              <span>
                <Button
                  variant="outlined"
                  color="warning"
                  size="small"
                  onClick={handleReconcileStale}
                  disabled={isReconciling}
                  startIcon={isReconciling ? <CircularProgress size={14} /> : <HealingOutlined fontSize="small" />}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  {isReconciling ? 'Resolving…' : 'Resolve Stale Builds'}
                </Button>
              </span>
            </Tooltip>
          </Stack>

          <Box sx={{ ...dataGridContainerSx, width: '100%', maxWidth: '100% !important', minHeight: 400 }}>
            <DataGrid<PipelineData>
              rows={
                rows
                  .filter(r => {
                    if (feedFilter === 'all') return true;
                    const s = (r.pipelineStatus || r.status || '').toLowerCase();
                    if (feedFilter === 'failed') return /fail|cancel/.test(s);
                    if (feedFilter === 'running') return /queue|running|pending/.test(s);
                    if (feedFilter === 'mine') return matchesCurrentUser(r.submitter, userInfo);
                    if (feedFilter === 'lastHour') {
                      const t = new Date(r.dateTimeStamp || '').getTime();
                      return Number.isFinite(t) && t >= Date.now() - 60 * 60 * 1000;
                    }
                    return true;
                  })
              }
              onRowClick={(params, event) => {
                // Don't hijack clicks on the row's own links/buttons (View Log, retry, cancel).
                if ((event.target as HTMLElement)?.closest?.('a,button')) return;
                const r = params.row as PipelineData;
                setErrorDrawerBuild({
                  id: r.id,
                  pipelineId: r.pipelineId,
                  composerHost: r.composerHost,
                  appName: r.composerHost,
                  version: r.version,
                  platform: r.osSpecification,
                  status: r.pipelineStatus || r.status,
                  submitter: r.submitter,
                  pipelineUrl: r.pipelineUrl,
                });
              }}
              columns={columns}
              getRowId={(row) => row.id}
              autoHeight
              pagination
              paginationMode="server"
              rowCount={rowCount}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[10, 15, 25]}
              disableRowSelectionOnClick
              loading={loading}
              rowHeight={56}
              sx={{ ...dataGridSx, '& .MuiDataGrid-row': { cursor: 'pointer' } }}
            />
          </Box>
        </motion.div>
      </Box>

      <BuildErrorDrawer build={errorDrawerBuild} onClose={() => setErrorDrawerBuild(null)} />

      <div ref={bottomRef} />

      {showScrollUp && (
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} whileHover={{ scale: 1.15, boxShadow: '0 0 18px rgba(156,39,176,0.75)' }} whileTap={{ scale: 0.95 }} style={{ position: 'fixed', bottom: 20, right: 20, zIndex: 1300 }}>
          <Fab color="secondary" onClick={handleScrollUp} aria-label="Back to top"><ArrowUpward /></Fab>
        </motion.div>
      )}
    </>
  );
};